// Billing Rates — the rate card that prices billable services. Rates may be
// global (the company default card, organization_id = null) or scoped to one
// client org that overrides the default. The list filters by client / global /
// service type; create + edit happen in a modal.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import type {
  BillingRate,
  BillingServiceType,
  Organization,
  RateType,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import { useOrganizations } from '../api/queries/clients';
import {
  useBillingRates,
  useCreateBillingRate,
  useUpdateBillingRate,
  useDeleteBillingRate,
  type BillingRateFilters,
} from '../api/queries/billing';

// ---------- Static option lists ----------

const SERVICE_TYPES: { value: BillingServiceType; label: string }[] = [
  { value: 'unarmed_hourly', label: 'Unarmed guard (hourly)' },
  { value: 'armed_hourly', label: 'Armed guard (hourly)' },
  { value: 'ep_hourly', label: 'Executive protection (hourly)' },
  { value: 'ep_daily', label: 'Executive protection (daily)' },
  { value: 'patrol_visit', label: 'Patrol visit' },
  { value: 'gsoc_monthly', label: 'GSOC monitoring (monthly)' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'event_flat', label: 'Event (flat)' },
];

const RATE_TYPES: { value: RateType; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'flat', label: 'Flat' },
  { value: 'monthly', label: 'Monthly' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtMoney(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function serviceLabel(t: BillingServiceType): string {
  return SERVICE_TYPES.find((s) => s.value === t)?.label ?? t;
}

function rateLabel(t: RateType): string {
  return RATE_TYPES.find((r) => r.value === t)?.label ?? t;
}

// =============================================================================
// Page
// =============================================================================

export function BillingRatesPage(): JSX.Element {
  const canWrite = can('billing_rate.write');

  const [filters, setFilters] = useState<BillingRateFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<BillingRate | null>(null);
  const [deleting, setDeleting] = useState<BillingRate | null>(null);

  const ratesQuery = useBillingRates(filters);
  const orgsQuery = useOrganizations();
  const deleteRate = useDeleteBillingRate();

  const rates = ratesQuery.data?.billing_rates ?? [];
  const orgs = orgsQuery.data?.organizations ?? [];

  const orgName = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of orgs) map.set(o.id, o.name);
    return (id: number | null): string =>
      id === null ? 'Global default' : map.get(id) ?? `Org #${id}`;
  }, [orgs]);

  // Standalone filter options. Radix Select forbids an empty value, so the
  // "all clients" choice uses the literal 'all' sentinel; 'global' selects the
  // company default card, and a numeric string selects one client org.
  const clientFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All' },
    { value: 'global', label: 'Global default' },
    ...orgs.map((o) => ({ value: String(o.id), label: o.name })),
  ];
  const clientFilterValue = filters.global
    ? 'global'
    : filters.organization_id != null
      ? String(filters.organization_id)
      : 'all';

  const serviceFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All services' },
    ...SERVICE_TYPES.map((s) => ({ value: s.value, label: s.label })),
  ];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteRate.mutateAsync(deleting.id);
      toast.success('Deleted billing rate.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete billing rate.'));
    }
  };

  return (
    <div className="billing-rates-page">
      <header className="page-header">
        <div>
          <h2>Billing Rates</h2>
          <p className="page-sub">
            The rate card that prices billable services — global defaults plus
            per-client overrides.
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New rate
          </Button>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={clientFilterValue}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              global: v === 'global' ? true : undefined,
              organization_id:
                v === 'global' || v === 'all' ? undefined : Number(v),
            }))
          }
          options={clientFilterOptions}
          aria-label="Filter by client"
        />

        <Select
          value={filters.service_type ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              service_type:
                v === 'all' ? undefined : (v as BillingServiceType),
            }))
          }
          options={serviceFilterOptions}
          aria-label="Filter by service"
        />
      </div>

      {ratesQuery.isLoading && <p className="muted">Loading rates…</p>}
      {ratesQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(ratesQuery.error, 'Failed to load billing rates.')}
        </p>
      )}
      {!ratesQuery.isLoading && rates.length === 0 && (
        <p className="muted empty-state">No billing rates match these filters.</p>
      )}

      {rates.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Scope</Table.HeaderCell>
              <Table.HeaderCell>Service</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Rate type</Table.HeaderCell>
              <Table.HeaderCell>Notes</Table.HeaderCell>
              {canWrite && <Table.HeaderCell className="col-actions">Actions</Table.HeaderCell>}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {rates.map((r) => (
              <Table.Row key={r.id}>
                <Table.Cell tone="strong">
                  {r.organization_id === null ? (
                    <Badge tone="neutral">Global default</Badge>
                  ) : (
                    orgName(r.organization_id)
                  )}
                </Table.Cell>
                <Table.Cell>{serviceLabel(r.service_type)}</Table.Cell>
                <Table.Cell>{fmtMoney(r.amount)}</Table.Cell>
                <Table.Cell tone="muted">{rateLabel(r.rate_type)}</Table.Cell>
                <Table.Cell tone="muted" className="cell-truncate">{r.notes ?? '—'}</Table.Cell>
                {canWrite && (
                  <Table.Cell className="col-actions">
                    <Button
                      variant="ghost"
                      className="btn-sm"
                      onClick={() => setEditing(r)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => setDeleting(r)}
                    >
                      Delete
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {/* Create */}
      {createOpen && (
        <RateFormModal mode="create" orgs={orgs} onClose={() => setCreateOpen(false)} />
      )}

      {/* Edit */}
      {editing && (
        <RateFormModal
          mode="edit"
          rate={editing}
          orgs={orgs}
          onClose={() => setEditing(null)}
        />
      )}

      {/* Delete */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete billing rate"
        message={
          deleting
            ? `Delete the ${serviceLabel(deleting.service_type)} rate for ${orgName(
                deleting.organization_id,
              )}?`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteRate.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Create / edit modal
// =============================================================================

interface RateFormModalProps {
  mode: 'create' | 'edit';
  rate?: BillingRate;
  orgs: Organization[];
  onClose: () => void;
}

function RateFormModal({ mode, rate, orgs, onClose }: RateFormModalProps): JSX.Element {
  const create = useCreateBillingRate();
  const update = useUpdateBillingRate();
  const busy = create.isPending || update.isPending;

  // '' represents the global (organization_id = null) scope.
  const [scope, setScope] = useState<number | ''>(
    rate ? (rate.organization_id ?? '') : '',
  );
  const [serviceType, setServiceType] = useState<BillingServiceType>(
    rate?.service_type ?? 'unarmed_hourly',
  );
  const [rateType, setRateType] = useState<RateType>(rate?.rate_type ?? 'hourly');
  const [amount, setAmount] = useState(rate?.amount ?? '');
  const [notes, setNotes] = useState(rate?.notes ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedAmount = amount.trim();
    if (!/^\d+(\.\d{1,2})?$/.test(trimmedAmount)) {
      toast.warn('Amount must be a non-negative number (e.g. 45.00).');
      return;
    }
    const trimmedNotes = notes.trim();

    try {
      if (mode === 'create') {
        await create.mutateAsync({
          organization_id: scope === '' ? null : scope,
          service_type: serviceType,
          amount: trimmedAmount,
          rate_type: rateType,
          ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        });
        toast.success('Created billing rate.');
      } else if (rate) {
        // The scope (organization_id) is fixed for the life of a rate; only
        // the priced attributes are editable.
        await update.mutateAsync({
          id: rate.id,
          service_type: serviceType,
          amount: trimmedAmount,
          rate_type: rateType,
          notes: trimmedNotes ? trimmedNotes : null,
        });
        toast.success('Billing rate updated.');
      }
      onClose();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save billing rate.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'New billing rate' : 'Edit billing rate'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="rate-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="rate-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Scope">
          <select
            className="form-input"
            value={scope}
            onChange={(e) => setScope(e.target.value ? Number(e.target.value) : '')}
            disabled={mode === 'edit'}
          >
            <option value="">Global default</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </FormField>

        <FormRow>
          <FormField label="Service">
            <select
              className="form-input"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value as BillingServiceType)}
            >
              {SERVICE_TYPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Rate type">
            <select
              className="form-input"
              value={rateType}
              onChange={(e) => setRateType(e.target.value as RateType)}
            >
              {RATE_TYPES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormField label="Amount (USD)" required>
          <Input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            required
            autoFocus
          />
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            placeholder="Optional"
          />
        </FormField>
      </form>
    </Dialog>
  );
}
